// Route: /tools/essay — список эссе пользователя.
// URL state: ?status=draft|queued|grading|ready|failed, ?q=, ?demo=loading|empty|error
//
// Тактильный редизайн «Картотека»: заголовок с иконкой-плиткой; статус-фильтр —
// ряд тактильных pill-чипов; список — таблица в e2-панели (desktop) или
// тактильные карточки (mobile); пустое состояние — «старт» с CTA-клавишей.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button, EmptyState, ErrorState, SectionHeader, Skeleton } from "@studyhub/ui";
import { SegmentedControl } from "@studyhub/ui/components/segmented-control";
import { toast } from "@studyhub/ui/toast";
import { IconPencil, IconPlus } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { EssayStatus } from "@/components/essays/_api";
import { EssayList } from "@/components/essays/essay-list";
import { NewEssayDialog } from "@/components/essays/new-essay-dialog";
import { SECTION_TINT } from "@/components/navigation/section-icons";
import { LIST_PAGE_SHELL, ListToolbar } from "@/components/shared/entity-list";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import {
	SectionIntro,
	SectionIntroReopen,
	useSectionIntro,
} from "@/components/shared/section-intro";
import { useArtifactWatching } from "@/hooks/use-artifact-watching";
import { useConfirm } from "@/hooks/use-confirm";
import { useEssayRealtimeSync } from "@/hooks/use-essay-realtime-sync";
import { useDeleteEssay, useEssaysListLegacy } from "@/hooks/use-essays";
import { markArtifactSeenKey } from "@/hooks/use-mark-artifact-seen";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { useViewMode } from "@/hooks/use-view-mode";
import { toastError } from "@/lib/user-facing-error";

const searchSchema = z.object({
	status: z.enum(["draft", "queued", "grading", "ready", "failed"]).optional(),
	q: z.string().optional(),
	demo: z
		.enum(["default", "loading", "empty", "error", "processing", "graded", "partial"])
		.optional(),
	/** Авто-открыть диалог создания эссе (точка входа с дашборда). */
	create: z.boolean().optional(),
});

export const Route = createFileRoute("/tools/essay/")({
	validateSearch: tolerantSearch(searchSchema),
	component: EssaysListPage,
	pendingComponent: PagePendingFallback,
});

const STATUS_FILTERS: ReadonlyArray<EssayStatus | "all"> = [
	"all",
	"draft",
	"queued",
	"grading",
	"ready",
	"failed",
];

function EssaysListPage(): React.ReactElement {
	const { t } = useTranslation("essays");
	const { t: tTrash } = useTranslation("trash");
	const intro = useSectionIntro("essays");
	const search = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	const [newOpen, setNewOpen] = useState(false);
	const [view, setView] = useViewMode("essays");
	const online = useOnlineStatus();
	const confirm = useConfirm();

	const essaysQuery = useEssaysListLegacy({ status: search.status });
	const deleteMutation = useDeleteEssay();

	// Авто-открытие диалога создания при переходе с дашборда (?create=true).
	// Сразу очищаем флаг из URL — иначе reload/back повторно откроют диалог.
	useEffect(() => {
		if (!search.create) return;
		setNewOpen(true);
		void navigate({ search: (prev) => ({ ...prev, create: undefined }), replace: true });
	}, [search.create, navigate]);

	// Real-time: grading завершается асинхронно (Inngest). Без подписки карточка
	// списка зависала в «Проверяется» до F5. Хук уже инвалидирует список на
	// essays:status_changed / essays:ready — монтируем без essayId (вся страница).
	useEssayRealtimeSync({ withToasts: false });

	const isLoading = search.demo === "loading" || essaysQuery.isLoading;
	const isError = search.demo === "error" || essaysQuery.isError;
	const isEmpty = search.demo === "empty";

	const items = useMemo(() => {
		if (isEmpty) return [];
		let list = essaysQuery.data ?? [];
		if (search.demo === "processing") {
			list = list.map((it) => ({
				...it,
				status: "grading" as EssayStatus,
				gradingProgress: 42,
			}));
		}
		if (search.demo === "graded") {
			list = list.map((it) => ({
				...it,
				status: "ready" as EssayStatus,
				overallScore: it.overallScore ?? 86,
			}));
		}
		if (search.demo === "partial") {
			list = list.map((it) => ({ ...it, requiresManualReview: true }));
		}
		if (search.q) {
			const q = search.q.toLowerCase();
			list = list.filter((it) => it.title.toLowerCase().includes(q));
		}
		return list;
	}, [essaysQuery.data, isEmpty, search.demo, search.q]);

	// Presence-aware: ждём grading/queued на списке → mute notify.
	const pendingEssayWatchKeys = useMemo(
		() =>
			items
				.filter((it) => it.status === "queued" || it.status === "grading")
				.map((it) => markArtifactSeenKey("essay", it.id)),
		[items],
	);
	useArtifactWatching(pendingEssayWatchKeys);

	const handleAfterSubmit = (essayId: string) => {
		void navigate({ to: "/tools/essay/$id", params: { id: essayId } });
	};

	const handleDelete = async (id: string): Promise<void> => {
		if (!online) {
			toast.error(tTrash("offline.action"));
			return;
		}
		const ok = await confirm({
			title: t("delete.title", { defaultValue: "Удалить эссе?" }),
			description: t("delete.subtitle"),
			confirmLabel: t("delete.confirm", { defaultValue: "Удалить" }),
			tone: "destructive",
		});
		if (!ok) return;
		try {
			await deleteMutation.mutateAsync({ essayId: id });
			toast.success(t("toast.deleted", { defaultValue: "Эссе удалено" }));
		} catch (err) {
			toastError(err, t("toast.deleteError", { defaultValue: "Не удалось удалить" }), t);
		}
	};

	const activeStatus = search.status ?? "all";

	return (
		<div className={LIST_PAGE_SHELL}>
			{/* Заголовок секции */}
			<SectionHeader
				category="ai"
				tint={SECTION_TINT.essay}
				icon={<IconPencil stroke={1.75} />}
				title={t("title")}
				description={t("subtitle")}
				stackActions
				actions={
					<>
						<SectionIntroReopen intro={intro} />
						<Button type="button" className="flex-1 sm:flex-none" onClick={() => setNewOpen(true)}>
							<IconPlus size={16} />
							{t("new.cta")}
						</Button>
					</>
				}
			/>

			{/* Баннер-объяснение раздела: текст из манифеста модуля, состояние
			    «свёрнут» — серверное (см. section-intro.tsx). */}
			<SectionIntro intro={intro} />

			{/* Единый тулбар: поиск + сегмент статуса + переключатель вида */}
			{!isError && !isLoading && (
				<ListToolbar
					search={{
						value: search.q ?? "",
						onChange: (q) =>
							void navigate({
								search: (prev) => ({ ...prev, q: q.trim() || undefined }),
								replace: true,
							}),
						placeholder: t("list.search"),
					}}
					segments={
						<SegmentedControl
							scrollable
							aria-label={t("filter.all")}
							value={activeStatus}
							onValueChange={(s) =>
								void navigate({
									search: (prev) => ({
										...prev,
										status: s === "all" ? undefined : (s as EssayStatus),
									}),
									replace: true,
								})
							}
							options={STATUS_FILTERS.map((s) => ({
								value: s,
								label: s === "all" ? t("filter.all") : t(`status.${s}`),
							}))}
						/>
					}
					view={view}
					onViewChange={setView}
				/>
			)}

			{/* Тело */}
			{isLoading ? (
				<div className="space-y-2">
					{Array.from({ length: 6 }).map((_, i) => (
						<Skeleton key={i} className="h-16 rounded-[var(--radius-card)]" />
					))}
				</div>
			) : isError ? (
				<ErrorState
					title={t("list.errorTitle")}
					description={t("list.errorSubtitle")}
					action={
						<Button variant="outline" onClick={() => void essaysQuery.refetch()}>
							{t("list.retry")}
						</Button>
					}
				/>
			) : items.length === 0 ? (
				<EmptyState
					icon={<IconPencil size={48} className="text-muted-foreground" stroke={1.5} />}
					title={t("list.empty.title")}
					description={t("list.empty.subtitle")}
					action={
						<Button onClick={() => setNewOpen(true)}>
							<IconPlus size={16} />
							{t("list.empty.cta")}
						</Button>
					}
				/>
			) : (
				<EssayList items={items} view={view} onDelete={(id) => void handleDelete(id)} />
			)}

			<NewEssayDialog open={newOpen} onOpenChange={setNewOpen} onCreated={handleAfterSubmit} />
		</div>
	);
}
